import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Alert, CircularProgress, Avatar, Tooltip, IconButton,
  LinearProgress, InputAdornment, List, ListItem, ListItemText,
} from '@mui/material';
import {
  EventBusy, Add, Edit, Warning, Search, GppBad,
  TrendingUp, Dangerous,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const nMonthsAgo = (n: number) => { const d = new Date(); d.setMonth(d.getMonth() - n); return d.toISOString().slice(0, 10); };
const today = () => new Date().toISOString().slice(0, 10);

const TYPE_CFG: Record<string, { label: string; color: string; emoji: string }> = {
  unauthorised:    { label: 'Unauthorised',      color: '#e65100', emoji: '🚫' },
  awol:            { label: 'AWOL',              color: '#b71c1c', emoji: '⚠️' },
  late_arrival:    { label: 'Late Arrival',      color: '#f57f17', emoji: '🕐' },
  early_departure: { label: 'Early Departure',   color: '#f9a825', emoji: '🏃' },
  unplanned_sick:  { label: 'Unplanned Sick',    color: '#6a1b9a', emoji: '🤒' },
  no_show:         { label: 'No Show',           color: '#c62828', emoji: '❌' },
  partial_day:     { label: 'Partial Day',       color: '#1565c0', emoji: '📅' },
  other:           { label: 'Other',             color: '#546e7a', emoji: '📋' },
};

const STATUS_CFG: Record<string, { label: string; color: string; bg: string }> = {
  recorded:      { label: 'Recorded',      color: '#546e7a', bg: '#eceff1' },
  explained:     { label: 'Explained',     color: '#1565c0', bg: '#e3f2fd' },
  excused:       { label: 'Excused',       color: '#2e7d32', bg: '#e8f5e9' },
  disciplinary:  { label: 'Disciplinary',  color: '#b71c1c', bg: '#ffebee' },
  resolved:      { label: 'Resolved',      color: '#37474f', bg: '#f5f5f5' },
};

const SEV_CFG: Record<string, { color: string; bg: string }> = {
  low:      { color: '#2e7d32', bg: '#e8f5e9' },
  medium:   { color: '#f57f17', bg: '#fffde7' },
  high:     { color: '#e65100', bg: '#fff3e0' },
  critical: { color: '#b71c1c', bg: '#ffebee' },
};

const BF_RISK: Record<string, { label: string; color: string; desc: string }> = {
  low:      { label: 'Low',      color: '#2e7d32', desc: 'Score < 150 — within acceptable range' },
  medium:   { label: 'Medium',   color: '#f57f17', desc: 'Score 150–449 — monitor closely' },
  high:     { label: 'High',     color: '#e65100', desc: 'Score 450–899 — formal review recommended' },
  critical: { label: 'Critical', color: '#b71c1c', desc: 'Score ≥ 900 — immediate action required' },
};

interface AbsenceRecord {
  id: string; employeeId: string; absenceDate: string; absenceEndDate?: string;
  daysAbsent: number; absenceType: string; status: string; severity: string;
  notes?: string; employeeExplanation?: string; resolutionNotes?: string;
  expectedArrival?: string; actualArrival?: string; lateMinutes?: number;
  warningIssued: boolean; disciplinaryRaised: boolean;
  employee?: { firstName: string; lastName: string; department?: { name: string }; position?: { title: string } };
}

interface BfRow { employeeId: string; name: string; dept: string; score: number; spells: number; totalDays: number; risk: string }

const emptyForm = {
  employeeId: '', absenceDate: today(), absenceEndDate: '', daysAbsent: 1,
  absenceType: 'unauthorised', severity: 'medium', notes: '',
  expectedArrival: '', actualArrival: '', lateMinutes: '',
};

export default function Absence() {
  const { user } = useAuth();
  const isHR  = ['hr_admin', 'hr_manager'].includes((user as any)?.role || '');
  const isMgr = isHR || (user as any)?.role === 'manager';

  const [tab, setTab] = useState(0);
  const [records, setRecords] = useState<AbsenceRecord[]>([]);
  const [bfRows, setBfRows]   = useState<BfRow[]>([]);
  const [stats, setStats]     = useState<any>(null);
  const [employees, setEmployees] = useState<any[]>([]);
  const [search, setSearch]   = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [fromDate, setFromDate] = useState(nMonthsAgo(3));
  const [toDate, setToDate]     = useState(today());
  const [loading, setLoading]  = useState(true);
  const [error, setError]      = useState('');

  const [selected, setSelected] = useState<AbsenceRecord | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [editId, setEditId]     = useState<string | null>(null);
  const [saving, setSaving]     = useState(false);
  const [statusDialog, setStatusDialog] = useState<{ id: string } | null>(null);
  const [newStatus, setNewStatus] = useState('explained');
  const [statusNote, setStatusNote] = useState('');

  const TABS = isMgr ? ['Absence Register', 'Bradford Factor', 'Dashboard'] : ['My Absences'];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ from: fromDate, to: toDate });
      if (typeFilter) params.set('type', typeFilter);
      if (!isMgr) params.set('employeeId', (user as any)?.employee?.id || '');

      const calls: Promise<any>[] = [apiFetch(`/api/absences?${params}`)];
      if (isMgr) {
        calls.push(apiFetch(`/api/absences/stats?from=${fromDate}&to=${toDate}`));
        calls.push(apiFetch('/api/employees?limit=300'));
      }
      if (isHR && tab === 1) calls.push(apiFetch('/api/absences/bradford'));

      const results = await Promise.allSettled(calls);
      if (results[0].status === 'fulfilled') setRecords(results[0].value ?? []);
      if (results[1] && results[1].status === 'fulfilled') setStats((results[1] as any).value);
      if (results[2] && results[2].status === 'fulfilled') {
        const d = (results[2] as any).value;
        setEmployees(Array.isArray(d) ? d : (d.data ?? []));
      }
      if (results[3] && results[3].status === 'fulfilled') setBfRows((results[3] as any).value ?? []);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [tab, fromDate, toDate, typeFilter, isMgr, isHR, user]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditId(null); setFormData({ ...emptyForm }); setFormOpen(true); };
  const openEdit = (r: AbsenceRecord) => {
    setEditId(r.id);
    setFormData({
      employeeId: r.employeeId, absenceDate: r.absenceDate, absenceEndDate: r.absenceEndDate || '',
      daysAbsent: r.daysAbsent, absenceType: r.absenceType, severity: r.severity,
      notes: r.notes || '', expectedArrival: r.expectedArrival || '',
      actualArrival: r.actualArrival || '', lateMinutes: r.lateMinutes?.toString() || '',
    });
    setFormOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...formData, lateMinutes: formData.lateMinutes ? parseInt(formData.lateMinutes as any) : undefined };
      if (editId) {
        await apiFetch(`/api/absences/${editId}`, { method: 'PATCH', body: JSON.stringify(payload) });
      } else {
        await apiFetch('/api/absences', { method: 'POST', body: JSON.stringify(payload) });
      }
      setFormOpen(false); load();
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const changeStatus = async () => {
    if (!statusDialog) return;
    await apiFetch(`/api/absences/${statusDialog.id}/status`, {
      method: 'PATCH', body: JSON.stringify({ status: newStatus, notes: statusNote }),
    });
    setStatusDialog(null); load();
  };

  const filtered = records.filter(r => {
    if (!search) return true;
    const name = `${r.employee?.firstName ?? ''} ${r.employee?.lastName ?? ''}`.toLowerCase();
    return name.includes(search.toLowerCase());
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#b71c1c', width: 48, height: 48 }}><EventBusy /></Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>Absence Management</Typography>
            <Typography variant="body2" color="text.secondary">Unauthorised absences · AWOL · late arrivals · Bradford Factor</Typography>
          </Box>
        </Box>
        {isMgr && <Button variant="contained" startIcon={<Add />} onClick={openCreate} sx={{ bgcolor: '#b71c1c', '&:hover': { bgcolor: '#7f0000' } }}>Record Absence</Button>}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Stats row */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            { label: 'Total Records',  value: stats.total,                                                         color: '#546e7a' },
            { label: 'AWOL',           value: stats.byType?.find((t: any) => t.type === 'awol')?.count ?? 0,      color: '#b71c1c' },
            { label: 'Unauthorised',   value: stats.byType?.find((t: any) => t.type === 'unauthorised')?.count ?? 0, color: '#e65100' },
            { label: 'Late Arrivals',  value: stats.byType?.find((t: any) => t.type === 'late_arrival')?.count ?? 0, color: '#f57f17' },
            { label: 'Unresolved',     value: stats.byStatus?.filter((s: any) => s.status === 'recorded' || s.status === 'explained').reduce((a: number, s: any) => a + parseInt(s.count), 0) ?? 0, color: '#1565c0' },
          ].map(s => (
            <Grid size={{ xs: 6, sm: 4, md: 2 }} key={s.label}>
              <Paper sx={{ p: 1.5, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
                <Typography variant="h4" fontWeight={700} color={s.color}>{s.value}</Typography>
                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {stats?.recentAWOL?.length > 0 && (
        <Alert severity="error" icon={<Dangerous />} sx={{ mb: 2 }}>
          <strong>{stats.recentAWOL.length} AWOL record{stats.recentAWOL.length !== 1 ? 's' : ''}</strong> on file —{' '}
          {stats.recentAWOL.map((r: any) => `${r.employee?.firstName} ${r.employee?.lastName}`).join(', ')}.
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        {TABS.map((t, i) => <Tab key={i} label={t} />)}
      </Tabs>

      {/* ── Absence Register / My Absences ── */}
      {(tab === 0) && (
        <>
          <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            {isMgr && (
              <TextField size="small" placeholder="Search employee…" value={search}
                onChange={e => setSearch(e.target.value)} sx={{ minWidth: 200 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
            )}
            <TextField select size="small" label="Type" value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)} sx={{ minWidth: 160 }}>
              <MenuItem value="">All Types</MenuItem>
              {Object.entries(TYPE_CFG).map(([k, v]) => <MenuItem key={k} value={k}>{v.emoji} {v.label}</MenuItem>)}
            </TextField>
            <TextField size="small" label="From" type="date" InputLabelProps={{ shrink: true }}
              value={fromDate} onChange={e => setFromDate(e.target.value)} />
            <TextField size="small" label="To" type="date" InputLabelProps={{ shrink: true }}
              value={toDate} onChange={e => setToDate(e.target.value)} />
          </Paper>

          {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box> : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {isMgr && <TableCell>Employee</TableCell>}
                    <TableCell>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Days</TableCell>
                    <TableCell>Severity</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Flags</TableCell>
                    {isMgr && <TableCell>Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 && (
                    <TableRow><TableCell colSpan={isMgr ? 8 : 6} align="center" sx={{ py: 4, color: 'text.secondary' }}>No records found</TableCell></TableRow>
                  )}
                  {filtered.map(r => {
                    const tc = TYPE_CFG[r.absenceType] ?? TYPE_CFG.other;
                    const sc = STATUS_CFG[r.status] ?? STATUS_CFG.recorded;
                    const sev = SEV_CFG[r.severity] ?? SEV_CFG.low;
                    return (
                      <TableRow key={r.id} hover onClick={() => setSelected(r)} sx={{ cursor: 'pointer', bgcolor: r.absenceType === 'awol' ? '#ffebee20' : undefined }}>
                        {isMgr && (
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>{r.employee?.firstName} {r.employee?.lastName}</Typography>
                            <Typography variant="caption" color="text.secondary">{r.employee?.department?.name}</Typography>
                          </TableCell>
                        )}
                        <TableCell>
                          <Typography variant="body2">{r.absenceDate}</Typography>
                          {r.absenceEndDate && r.absenceEndDate !== r.absenceDate && (
                            <Typography variant="caption" color="text.secondary">→ {r.absenceEndDate}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={`${tc.emoji} ${tc.label}`}
                            sx={{ bgcolor: tc.color + '15', color: tc.color, fontSize: '0.65rem' }} />
                        </TableCell>
                        <TableCell align="right"><Typography variant="body2" fontWeight={600}>{r.daysAbsent}</Typography></TableCell>
                        <TableCell>
                          <Chip size="small" label={r.severity}
                            sx={{ bgcolor: sev.bg, color: sev.color, fontSize: '0.65rem', fontWeight: 600 }} />
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={sc.label}
                            sx={{ bgcolor: sc.bg, color: sc.color, fontSize: '0.65rem' }} />
                        </TableCell>
                        <TableCell>
                          {r.warningIssued && <Chip size="small" label="⚠️ Warning" sx={{ mr: 0.5, fontSize: '0.6rem', bgcolor: '#fff3e0', color: '#e65100' }} />}
                          {r.disciplinaryRaised && <Chip size="small" label="🚨 Disciplinary" sx={{ fontSize: '0.6rem', bgcolor: '#ffebee', color: '#b71c1c' }} />}
                        </TableCell>
                        {isMgr && (
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="Edit"><IconButton size="small" onClick={e => { e.stopPropagation(); openEdit(r); }}><Edit fontSize="small" /></IconButton></Tooltip>
                              <Button size="small" variant="outlined" onClick={e => { e.stopPropagation(); setStatusDialog({ id: r.id }); setNewStatus('excused'); setStatusNote(''); }}>Update</Button>
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* ── Bradford Factor ── */}
      {tab === 1 && isMgr && (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Bradford Factor</strong> = S² × D (S = number of absence spells in 52 weeks, D = total days absent).
            Scores ≥ 900 are critical; ≥ 450 warrant formal review; ≥ 150 need monitoring.
          </Alert>
          {loading ? <CircularProgress /> : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell align="right">Spells</TableCell>
                    <TableCell align="right">Total Days</TableCell>
                    <TableCell align="right">BF Score</TableCell>
                    <TableCell align="center">Risk Level</TableCell>
                    <TableCell>Score Bar</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bfRows.length === 0 && (
                    <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No absence data on file</TableCell></TableRow>
                  )}
                  {bfRows.map((row, i) => {
                    const rc = BF_RISK[row.risk] ?? BF_RISK.low;
                    const maxScore = Math.max(...bfRows.map(r => r.score), 1);
                    return (
                      <TableRow key={row.employeeId} hover sx={{ bgcolor: row.risk === 'critical' ? '#ffebee20' : undefined }}>
                        <TableCell><Typography variant="body2" fontWeight={600}>{row.name}</Typography></TableCell>
                        <TableCell><Typography variant="caption">{row.dept}</Typography></TableCell>
                        <TableCell align="right">{row.spells}</TableCell>
                        <TableCell align="right">{row.totalDays}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: rc.color }}>{row.score}</TableCell>
                        <TableCell align="center">
                          <Chip size="small" label={rc.label}
                            sx={{ bgcolor: rc.color + '15', color: rc.color, fontWeight: 700, fontSize: '0.65rem' }} />
                        </TableCell>
                        <TableCell sx={{ minWidth: 160 }}>
                          <LinearProgress variant="determinate" value={Math.round((row.score / maxScore) * 100)}
                            sx={{ height: 8, borderRadius: 4,
                              '& .MuiLinearProgress-bar': { bgcolor: rc.color } }} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* ── Dashboard ── */}
      {tab === 2 && isMgr && stats && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>By Type</Typography>
              {stats.byType?.map((t: any) => {
                const cfg = TYPE_CFG[t.type] ?? TYPE_CFG.other;
                return (
                  <Box key={t.type} sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                      <Typography variant="body2">{cfg.emoji} {cfg.label}</Typography>
                      <Typography variant="body2" fontWeight={600}>{t.count}</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={Math.round((parseInt(t.count) / (stats.total || 1)) * 100)}
                      sx={{ height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { bgcolor: cfg.color } }} />
                  </Box>
                );
              })}
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Top Absentees (period)</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>Employee</TableCell><TableCell align="right">Absences</TableCell><TableCell align="right">Days</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {stats.topAbsentees?.map((r: any, i: number) => (
                      <TableRow key={i} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{r.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{r.dept}</Typography>
                        </TableCell>
                        <TableCell align="right">{r.count}</TableCell>
                        <TableCell align="right">{parseFloat(r.totaldays).toFixed(1)}</TableCell>
                      </TableRow>
                    ))}
                    {!stats.topAbsentees?.length && <TableRow><TableCell colSpan={3} align="center" sx={{ color: 'text.secondary' }}>No data</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Detail side panel */}
      {selected && (
        <Dialog open onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {TYPE_CFG[selected.absenceType]?.emoji} Absence Detail
            <Typography variant="body2" color="text.secondary">
              {selected.employee?.firstName} {selected.employee?.lastName} · {selected.absenceDate}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Chip size="small" label={TYPE_CFG[selected.absenceType]?.label} sx={{ bgcolor: TYPE_CFG[selected.absenceType]?.color + '15', color: TYPE_CFG[selected.absenceType]?.color }} />
              <Chip size="small" label={STATUS_CFG[selected.status]?.label} sx={{ bgcolor: STATUS_CFG[selected.status]?.bg, color: STATUS_CFG[selected.status]?.color }} />
              <Chip size="small" label={selected.severity} sx={{ bgcolor: SEV_CFG[selected.severity]?.bg, color: SEV_CFG[selected.severity]?.color }} />
            </Box>
            {selected.lateMinutes && <Typography variant="body2" sx={{ mb: 1 }}>⏱ <strong>{selected.lateMinutes} minutes late</strong> (expected {selected.expectedArrival}, arrived {selected.actualArrival})</Typography>}
            {selected.notes && <><Typography variant="subtitle2" fontWeight={700}>HR Notes</Typography><Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{selected.notes}</Typography></>}
            {selected.employeeExplanation && <><Typography variant="subtitle2" fontWeight={700}>Employee Explanation</Typography><Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{selected.employeeExplanation}</Typography></>}
            {selected.resolutionNotes && <><Typography variant="subtitle2" fontWeight={700}>Resolution Notes</Typography><Typography variant="body2" color="text.secondary">{selected.resolutionNotes}</Typography></>}
          </DialogContent>
          <DialogActions><Button onClick={() => setSelected(null)}>Close</Button></DialogActions>
        </Dialog>
      )}

      {/* Create/Edit */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? 'Edit Absence' : 'Record Absence'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {!editId && (
            <TextField select fullWidth label="Employee" value={formData.employeeId}
              onChange={e => setFormData(p => ({ ...p, employeeId: e.target.value }))} required>
              {employees.map(e => <MenuItem key={e.id} value={e.id}>{e.firstName} {e.lastName} — {e.department?.name}</MenuItem>)}
            </TextField>
          )}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth label="Absence Date" type="date" InputLabelProps={{ shrink: true }}
              value={formData.absenceDate} onChange={e => setFormData(p => ({ ...p, absenceDate: e.target.value }))} required />
            <TextField fullWidth label="End Date (multi-day)" type="date" InputLabelProps={{ shrink: true }}
              value={formData.absenceEndDate} onChange={e => setFormData(p => ({ ...p, absenceEndDate: e.target.value }))} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField select fullWidth label="Absence Type" value={formData.absenceType}
              onChange={e => setFormData(p => ({ ...p, absenceType: e.target.value }))}>
              {Object.entries(TYPE_CFG).map(([k, v]) => <MenuItem key={k} value={k}>{v.emoji} {v.label}</MenuItem>)}
            </TextField>
            <TextField select fullWidth label="Severity" value={formData.severity}
              onChange={e => setFormData(p => ({ ...p, severity: e.target.value }))}>
              {Object.entries(SEV_CFG).map(([k]) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
            </TextField>
          </Box>
          {formData.absenceType === 'late_arrival' && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField fullWidth label="Expected Arrival" value={formData.expectedArrival} onChange={e => setFormData(p => ({ ...p, expectedArrival: e.target.value }))} placeholder="09:00" />
              <TextField fullWidth label="Actual Arrival" value={formData.actualArrival} onChange={e => setFormData(p => ({ ...p, actualArrival: e.target.value }))} placeholder="10:30" />
              <TextField fullWidth label="Minutes Late" type="number" value={formData.lateMinutes} onChange={e => setFormData(p => ({ ...p, lateMinutes: e.target.value }))} />
            </Box>
          )}
          <TextField fullWidth label="Notes" multiline rows={2} value={formData.notes}
            onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving || (!editId && !formData.employeeId)}
            sx={{ bgcolor: '#b71c1c' }}>{saving ? 'Saving…' : editId ? 'Update' : 'Record'}</Button>
        </DialogActions>
      </Dialog>

      {/* Status update */}
      <Dialog open={!!statusDialog} onClose={() => setStatusDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Update Status</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField select fullWidth label="New Status" value={newStatus} onChange={e => setNewStatus(e.target.value)}>
            {Object.entries(STATUS_CFG).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
          </TextField>
          <TextField fullWidth label="Resolution Notes" multiline rows={2} value={statusNote} onChange={e => setStatusNote(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={changeStatus} sx={{ bgcolor: '#b71c1c' }}>Update</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
